import {
  useCanAccess,
  useGetIdentity,
  useGetList,
  useTranslate,
} from "ra-core";
import { useMemo } from "react";

import type { Sale } from "../types";
import type { FilterChoice } from "./ChoiceFilter";
import { NULL_FILTER_VALUE } from "./listFilterValue";

const LEAD_SCORES = [5, 4, 3, 2, 1];
const MAX_SALES = 100;

/** 5 étoiles to 1 étoile, for a lead score or a rating custom field. */
export const useStarChoices = (): FilterChoice[] => {
  const translate = useTranslate();
  return LEAD_SCORES.map((score) => ({
    value: String(score),
    label: translate("crm.filters.stars", { smart_count: score }),
  }));
};

/**
 * Sales reps for a « Commercial » filter: me first, the others when the user
 * may list them, then « À définir » for records with no rep.
 */
export const useSalesFilterChoices = (): FilterChoice[] => {
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { canAccess } = useCanAccess({ resource: "sales", action: "list" });
  const { data: sales } = useGetList<Sale>(
    "sales",
    {
      pagination: { page: 1, perPage: MAX_SALES },
      sort: { field: "last_name", order: "ASC" },
      filter: { "disabled@neq": true },
    },
    { enabled: canAccess === true },
  );

  return useMemo(() => {
    const me = identity
      ? [{ value: String(identity.id), label: translate("crm.common.me") }]
      : [];
    const others = (sales ?? [])
      .filter((sale) => sale.id !== identity?.id)
      .map((sale) => ({
        value: String(sale.id),
        label: `${sale.first_name} ${sale.last_name}`,
      }));
    return [
      ...me,
      ...others,
      { value: NULL_FILTER_VALUE, label: translate("crm.common.to_define") },
    ];
  }, [identity, sales, translate]);
};
